import { supabase } from '@/lib/supabase'

const DEFAULT_BUCKETS = ['public', 'branding', 'assets']
const AVATAR_BUCKET = (import.meta as any).env?.VITE_AVATARS_BUCKET || 'public'

function slugifyBase(filename: string): string {
  const base = filename.replace(/\.[^/.]+$/, '')
  return base.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// Generic upload to a specific bucket and folder (SRP)
export async function uploadToBucket(
  bucket: string,
  file: File,
  folder = '',
): Promise<{ url: string; bucket: string; path: string }> {
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
  const key = `${folder ? folder + '/' : ''}${Date.now()}-${Math.random().toString(36).slice(2)}-${slugifyBase(file.name)}.${ext}`
  const { error } = await supabase.storage.from(bucket).upload(key, file, {
    upsert: false,
    contentType: file.type || undefined,
  })
  if (error) throw error
  const { data } = supabase.storage.from(bucket).getPublicUrl(key)
  return { url: data.publicUrl, bucket, path: key }
}

// Backward-compatible helper that tries a list of public buckets (YAGNI: keep minimal)
export async function uploadPublicFile(
  file: File,
  folder: string,
  buckets: string[] = DEFAULT_BUCKETS,
): Promise<{ url: string; bucket: string; path: string }> {
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
  const key = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}-${slugifyBase(file.name)}.${ext}`
  let lastErr: any = null
  for (const bucket of buckets) {
    try {
      const { error } = await supabase.storage.from(bucket).upload(key, file, { upsert: false })
      if (error) { lastErr = error; continue }
      const { data } = supabase.storage.from(bucket).getPublicUrl(key)
      return { url: data.publicUrl, bucket, path: key }
    } catch (e) { lastErr = e }
  }
  throw lastErr || new Error('Falha no upload')
}

export async function uploadPublicFileWithProgress(
  file: File,
  folder: string,
  onProgress?: (pct: number) => void,
  buckets: string[] = DEFAULT_BUCKETS,
): Promise<{ url: string; bucket: string; path: string }> {
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
  const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
  if (!supabaseUrl || !anon) throw new Error('Supabase URL/Anon key não configurados')
  const session = await supabase.auth.getSession()
  const token = session.data.session?.access_token || anon

  const base = (name: string) => name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
  const key = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}-${base(file.name)}.${ext}`

  let lastError: any = null
  for (const bucket of buckets) {
    try {
      const url = `${supabaseUrl.replace(/\/$/, '')}/storage/v1/object/${bucket}/${key}`
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('POST', url)
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        xhr.setRequestHeader('apikey', anon)
        xhr.setRequestHeader('Cache-Control', 'max-age=3600')
        try { xhr.setRequestHeader('x-upsert', 'true') } catch {}
        try { if (file.type) xhr.setRequestHeader('Content-Type', file.type) } catch {}
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(xhr.statusText || 'Upload failed')))
        xhr.onerror = () => reject(new Error('Upload error'))
        if (xhr.upload && onProgress) {
          xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return
            const pct = Math.max(0, Math.min(100, Math.round((e.loaded / e.total) * 100)))
            onProgress(pct)
          }
        }
        const fd = new FormData(); fd.append('file', file, file.name); xhr.send(fd)
      })
      const { data } = supabase.storage.from(bucket).getPublicUrl(key)
      onProgress?.(100)
      return { url: data.publicUrl, bucket, path: key }
    } catch (e) { lastError = e }
  }
  throw lastError || new Error('Falha no upload')
}

// Upload específico para logos de cripto com fallback via Edge Function service-role
export async function uploadLogoAsset(symbol: string, file: File): Promise<{ url: string; bucket: string; path: string }>{
  const folder = `logos/crypto-assets/${symbol}`
  try {
    // Preferir bucket 'assets'
    return await uploadToBucket('assets', file, folder)
  } catch (e: any) {
    const msg = String(e?.message || e)
    const needEdge = /Bucket not found|row-level security|Not Found|does not exist/i.test(msg)
    if (!needEdge) throw e
    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
    const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
    if (!supabaseUrl || !anon) throw new Error('Supabase URL/Anon key não configurados')
    const session = await supabase.auth.getSession()
    const token = session.data.session?.access_token || anon
    const fd = new FormData()
    fd.append('file', file, file.name)
    fd.append('bucket', 'assets')
    fd.append('path', folder)
    const resp = await fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/upload-public`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'apikey': anon }, body: fd })
    if (!resp.ok) throw new Error(await resp.text())
    return await resp.json()
  }
}
export async function uploadAvatarWithProgress(
  file: File,
  userId: string,
  onProgress?: (pct: number) => void,
): Promise<{ url: string; bucket: string; path: string }> {
  const folder = `avatars/users/${userId}`
  const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL as string
  const anon = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string
  if (!supabaseUrl || !anon) throw new Error('Supabase URL/Anon key não configurados')
  const session = await supabase.auth.getSession()
  const token = session.data.session?.access_token || anon
  const base = (name: string) => name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase()
  const key = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}-${base(file.name)}.${ext}`
  const bucket = (import.meta as any).env?.VITE_AVATARS_BUCKET || 'public'
  try {
    const url = `${supabaseUrl.replace(/\/$/, '')}/storage/v1/object/${bucket}/${key}`
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url)
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.setRequestHeader('apikey', anon)
      try { xhr.setRequestHeader('x-upsert', 'true') } catch {}
      try { if (file.type) xhr.setRequestHeader('Content-Type', file.type) } catch {}
      xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(xhr.statusText || 'Upload failed')))
      xhr.onerror = () => reject(new Error('Upload error'))
      if (xhr.upload && onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)) }
      const fd = new FormData(); fd.append('file', file, file.name); xhr.send(fd)
    })
    const { data } = supabase.storage.from(bucket).getPublicUrl(key)
    onProgress?.(100)
    return { url: data.publicUrl, bucket, path: key }
  } catch (err) {
    try {
      const { error } = await supabase.storage.from(bucket).upload(key, file, { upsert: true, contentType: file.type || undefined })
      if (error) throw error
      const { data } = supabase.storage.from(bucket).getPublicUrl(key)
      onProgress?.(100)
      return { url: data.publicUrl, bucket, path: key }
    } catch (e2) {
      // Final fallback: edge function with service role (no progress)
      const fd = new FormData()
      fd.append('file', file, file.name)
      const resp = await fetch(`${supabaseUrl.replace(/\/$/, '')}/functions/v1/upload-avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'apikey': anon,
        },
        body: fd,
      })
      if (!resp.ok) throw new Error(await resp.text())
      const payload = await resp.json()
      onProgress?.(100)
      return { url: payload.url, bucket: payload.bucket, path: payload.path }
    }
  }
}

